import { Form, FormikProvider, useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';

import { useErrorBoundary, useViewerRoles } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	ActionArchiveListItem,
	Button,
	FlexGap,
	GraphQLErrorMessage,
	Heading,
	Label,
	SettingsSection,
	Spinner,
	tokens,
	useToasts,
} from '@treams/treams-ui';

import DetailsTableForm from '../../../packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableForm';
import { DetailsTableFormValueTypes } from '../../../packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableFormValue';

export function SalaryScaleEditor(props: { salaryScale?: graphql.SalaryScale }) {
	const { t } = useTranslation();
	const [error, attempt] = useErrorBoundary();
	const { addToast } = useToasts();
	const history = useHistory();
	const { isHRBusinessPartner } = useViewerRoles();

	const [create, { loading: createLoading }] = graphql.useCreateSalaryScaleMutation();
	const [update, { loading: updateLoading }] = graphql.useUpdateSalaryScaleMutation();

	const formik = useFormik({
		initialValues: {
			name: props.salaryScale?.name ?? '',
			startAmount: props.salaryScale?.startAmount,
			endAmount: props.salaryScale?.endAmount,
		},
		onSubmit: attempt(async () => {
			const name = formik.values.name.trim();
			const startAmount = formik.values.startAmount;
			const endAmount = formik.values.endAmount;

			if (typeof startAmount !== 'number' || typeof endAmount !== 'number') {
				return;
			}

			if (props.salaryScale) {
				await update({
					variables: {
						input: {
							id: props.salaryScale.id,
							name,
							startAmount,
							endAmount,
						},
					},
				});
			} else {
				const response = await create({
					variables: {
						input: {
							name,
							startAmount,
							endAmount,
						},
					},
					refetchQueries: [graphql.SalaryScalesDocument],
					update(cache) {
						cache.evict({ fieldName: 'salaryScales' });
						cache.gc();
					},
				});

				history.push(
					`/settings/jobs/salary-scales/${response.data?.createSalaryScale.salaryScale.id}`,
				);
			}

			addToast(t('common.settings.salaryScales.edit.success'), {
				appearance: 'success',
				autoDismiss: true,
			});
		}),
	});

	const loading = createLoading || updateLoading;
	const canSave =
		formik.values.name.trim().length > 0 &&
		typeof formik.values.startAmount === 'number' &&
		typeof formik.values.endAmount === 'number';

	return (
		<FlexGap flexDirection="column" gap={tokens.gap.page} width="100%">
			{!isHRBusinessPartner && (
				<Heading mb={0}>
					{t(`common.settings.salaryScales.${props.salaryScale ? 'edit' : 'create'}.title`)}
				</Heading>
			)}
			<GraphQLErrorMessage error={error} />
			<FormikProvider value={formik}>
				{isHRBusinessPartner && (
					<Box>
						<Flex alignItems="center" flexWrap="wrap" justifyContent="space-between" minHeight={64}>
							<Box width={[12 / 12, 12 / 12, 3 / 12]} mb={[1, 1, 0]}>
								<Label>{t('common.settings.salaryScales.edit.name')}</Label>
							</Box>
							<Box width={[12 / 12, 12 / 12, 9 / 12]}>{props.salaryScale?.name ?? '-'}</Box>
						</Flex>
						<Flex alignItems="center" flexWrap="wrap" justifyContent="space-between" minHeight={64}>
							<Box width={[12 / 12, 12 / 12, 3 / 12]} mb={[1, 1, 0]}>
								<Label>{t('common.settings.salaryScales.edit.startAmount')}</Label>
							</Box>
							<Box width={[12 / 12, 12 / 12, 9 / 12]}>{props.salaryScale?.startAmount ?? '-'}</Box>
						</Flex>
						<Flex alignItems="center" flexWrap="wrap" justifyContent="space-between" minHeight={64}>
							<Box width={[12 / 12, 12 / 12, 3 / 12]} mb={[1, 1, 0]}>
								<Label>{t('common.settings.salaryScales.edit.endAmount')}</Label>
							</Box>
							<Box width={[12 / 12, 12 / 12, 9 / 12]}>{props.salaryScale?.endAmount ?? '-'}</Box>
						</Flex>
					</Box>
				)}
				{!isHRBusinessPartner && (
					<SettingsSection first title={t('common.settings.salaryScales.edit.details')}>
						<Form>
							<DetailsTableForm
								formValues={[
									{
										label: t('common.settings.salaryScales.edit.name'),
										name: 'name',
										type: DetailsTableFormValueTypes.Text,
									},
									{
										label: t('common.settings.salaryScales.edit.startAmount'),
										name: 'startAmount',
										type: DetailsTableFormValueTypes.Number,
									},
									{
										label: t('common.settings.salaryScales.edit.endAmount'),
										name: 'endAmount',
										type: DetailsTableFormValueTypes.Number,
									},
								]}
							/>
						</Form>
					</SettingsSection>
				)}
			</FormikProvider>

			{!isHRBusinessPartner && (
				<Flex justifyContent="flex-end">
					<Button
						role="button"
						aria-label={t('common.save')}
						onClick={() => formik.submitForm()}
						loading={loading}
						disabled={!canSave}
						title={t('common.save')}
					/>
				</Flex>
			)}
			{props.salaryScale && !isHRBusinessPartner && (
				<SettingsSection title={t('common.settings.salaryScales.actions.title')}>
					<ActionArchiveListItem
						archivableItem={props.salaryScale}
						archivableItemName={t('common.settings.salaryScales.archivableItemName')}
						archivableItemKey="salaryScale"
						useArchive={graphql.useArchiveSalaryScaleMutation}
						useRestore={graphql.useRestoreSalaryScaleMutation}
					/>
				</SettingsSection>
			)}
		</FlexGap>
	);
}

export default function SalaryScaleEditorWithID(props: { id: string }) {
	const { data } = graphql.useSalaryScaleQuery({
		variables: {
			id: props.id,
		},
		fetchPolicy: 'network-only',
	});

	if (!data) {
		return <Spinner centered />;
	}

	return <SalaryScaleEditor salaryScale={data.salaryScale} />;
}
